import { useState, type ButtonHTMLAttributes, type MouseEvent, type ReactNode } from "react";
import { Lock } from "lucide-react";
import AuthModal from "@/components/auth/AuthModal";
import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { SIGN_UP_ACTION, SIGN_UP_PROMPTS, type SignUpUnlock } from "./signUpPromptCopy";

type SignUpButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  /** Look of the sign-in view if the visitor switches to it; sign-up always uses the default look. */
  appearance?: "default" | "marketing";
  /** What the account unlocks, shown under the sign-up title. */
  unlock?: SignUpUnlock;
};

/**
 * A button that opens AuthModal in sign-up mode. The account type is AuthModal's
 * own default ("agent"); organization sign-up is unchanged and still needs an
 * invitation. Owns its open state and mounts the modal only while open, so the
 * prerender pass emits a bare <button> and needs no auth hook.
 */
export const SignUpButton = ({ onClick, children, appearance = "default", unlock, ...rest }: SignUpButtonProps) => {
  const [open, setOpen] = useState(false);

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    onClick?.(event);
    setOpen(true);
  };

  return (
    <>
      <button type="button" {...rest} onClick={handleClick}>
        {children}
      </button>
      {open && (
        <AuthModal
          isOpen={open}
          onClose={() => setOpen(false)}
          defaultMode="signup"
          allowSignup
          appearance={appearance}
          signUpIntro={unlock ? SIGN_UP_PROMPTS[unlock] : undefined}
        />
      )}
    </>
  );
};

/**
 * "Create a free account to …": a signed-out prompt that says what an account
 * unlocks, with a button that opens sign-up. Copy comes from signUpPromptCopy.
 */
export const SignUpPrompt = ({
  unlock,
  label,
  className,
  appearance = "marketing",
  onSignUp,
}: {
  unlock: SignUpUnlock;
  /**
   * Opens a sign-up the page already owns (the public org page's
   * PublicSignInProvider). Without it the prompt opens its own AuthModal.
   */
  onSignUp?: (unlock: SignUpUnlock) => void;
  /** Optional row heading, e.g. the hidden facts it stands in for. */
  label?: ReactNode;
  className?: string;
  appearance?: "default" | "marketing";
}) => (
  <div
    data-signup-prompt={unlock}
    className={cn(
      "flex flex-col gap-3 rounded-lg border border-border bg-muted/40 p-4 sm:flex-row sm:items-center sm:justify-between",
      className,
    )}
  >
    <div className="flex min-w-0 items-start gap-2.5">
      <Lock className="mt-0.5 h-4 w-4 shrink-0 text-ink-500" aria-hidden="true" />
      <div className="min-w-0">
        {label && (
          <p className="m-0 text-[12.5px] font-medium uppercase tracking-[0.06em] text-ink-500">{label}</p>
        )}
        <p className="m-0 text-[14.5px] font-medium text-ink-900">{SIGN_UP_PROMPTS[unlock]}</p>
      </div>
    </div>
    {onSignUp ? (
      <button type="button" onClick={() => onSignUp(unlock)} className={cn(buttonVariants({ size: "sm" }), "shrink-0")}>
        {SIGN_UP_ACTION}
      </button>
    ) : (
      <SignUpButton appearance={appearance} unlock={unlock} className={cn(buttonVariants({ size: "sm" }), "shrink-0")}>
        {SIGN_UP_ACTION}
      </SignUpButton>
    )}
  </div>
);

export default SignUpPrompt;
